import type { DinAttr } from "../administration/din276/din276Tree";

/**
 * Where an attribute's value sits in an object's `properties`.
 *
 * A plain attribute is a key of `properties`. A structure — an attribute a customer defined with
 * fields of its own (Betreiberpflichten → Datenmodell) — holds an object with one key per field,
 * nested as deep as the structure. A path names the way down: [attribute] or
 * [structure, …, field].
 */

/** A structure: its fields are shown as a group, its value is an object. */
export const isStructure = (attr: DinAttr) => Array.isArray(attr.children);

const isRecord = (value: unknown): value is Record<string, any> =>
  value != null && typeof value === "object" && !Array.isArray(value);

/** The value at `path`. */
export const valueAt = (properties: Record<string, any> | undefined, path: string[]): any =>
  path.reduce<any>((value, key) => (isRecord(value) ? value[key] : undefined), properties);

/** `properties` with the value at `path` replaced; the objects on the way are copied, not changed. */
export const withValueAt = (
  properties: Record<string, any> | undefined,
  path: string[],
  value: any,
): Record<string, any> => {
  const [key, ...rest] = path;
  const base = properties || {};
  return {
    ...base,
    [key]: rest.length === 0 ? value : withValueAt(isRecord(base[key]) ? base[key] : {}, rest, value),
  };
};
